import {useContext, useEffect, useRef, useState} from 'react'
import {GridActionsCellItem} from '@mui/x-data-grid'
import DataGridComponent from '../../components/DataGrid'
import ToolbarCustom from '../../../_metronic/layout/components/toolbar/toolbars/ToolbarCustom'
import {ProductsContext} from '../../context'
import CountryDetailModal from './CountryDetailModal'

const CountriesPage = () => {
  const {countries, countriesLoaded, fetchCountries, createCountry, updateCountry, deleteCountry} = useContext(ProductsContext)
  const [currentCountry, setCurrentCountry] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [loadError, setLoadError] = useState('')
  const [search, setSearch] = useState('')
  const fetching = useRef(false)

  useEffect(() => {
    if (!countriesLoaded && !fetching.current && !loadError) {
      fetching.current = true
      fetchCountries()
        .catch(() => setLoadError('Could not load countries. Please try again.'))
        .finally(() => { fetching.current = false })
    }
  }, [countriesLoaded, fetchCountries, loadError])

  const openCountry = (country) => {
    setError('')
    setCurrentCountry({...country})
  }

  const onDetailSubmit = async () => {
    if (loading) return
    const data = {...currentCountry, name: (currentCountry.name || '').trim(), code: (currentCountry.code || '').trim().toUpperCase()}
    if (!data.name || !/^[A-Z]{2}$/.test(data.code)) {
      setError('Enter a country name and a two-letter country code.')
      return
    }
    if (countries.some(country => country.id !== data.id && country.code?.trim().toUpperCase() === data.code)) {
      setError('A country with this code already exists.')
      return
    }
    setLoading(true)
    setError('')
    try {
      const response = data.id ? await updateCountry(data.id, data) : await createCountry(data)
      if (response?.error) throw response.error
      setCurrentCountry(null)
    } catch {
      setError('Could not save the country. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const onDetailDelete = async () => {
    if (loading || !window.confirm(`Are you sure you want to delete this country? (${currentCountry.name || currentCountry.code})`)) return
    setLoading(true)
    setError('')
    try {
      const response = await deleteCountry(currentCountry.id)
      if (response?.error) throw response.error
      setCurrentCountry(null)
    } catch {
      setError('Could not delete the country. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const query = search.trim().toLowerCase()
  const visibleCountries = countries
    .filter(country => `${country.name || ''} ${country.code || ''}`.toLowerCase().includes(query))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

  return (
    <>
      <ToolbarCustom
        pageTitle='Countries'
        primaryButton={countriesLoaded && {onClick: () => openCountry({id: '', name: '', code: ''}), title: 'Add'}}
        loading={!countriesLoaded && !loadError}
      />
      {loadError && <div className='alert alert-danger' role='alert'>
        {loadError} <button className='btn btn-sm btn-light' onClick={() => setLoadError('')}>Retry</button>
      </div>}
      {countriesLoaded && <>
        <input
          type='search'
          className='form-control form-control-solid mb-6'
          aria-label='Search countries'
          placeholder='Search by country name or code'
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
        <div className='mt-1'>
          <DataGridComponent
            id='countries'
            rows={visibleCountries}
            columns={[
              {field: 'name', headerName: 'Country', flex: 1, minWidth: 180},
              {field: 'code', headerName: 'Country code', width: 150},
              {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                width: 100,
                getActions: ({row}) => [
                  <GridActionsCellItem
                    key={`edit-${row.id}`}
                    icon={<i className='bi bi-pencil-square text-primary' />}
                    label={`Edit ${row.name || row.code}`}
                    onClick={() => openCountry(row)}
                    color='inherit'
                  />,
                ],
              },
            ]}
            pageSizeOptions={[20, 50, 100]}
          />
          {!visibleCountries.length && <p className='text-muted mt-4'>{query ? 'No countries match your search.' : 'No countries yet. Add a country to get started.'}</p>}
        </div>
      </>}
      <CountryDetailModal
        show={Boolean(currentCountry)}
        handleClose={() => { if (!loading) setCurrentCountry(null) }}
        country={currentCountry}
        setCountry={setCurrentCountry}
        onSubmit={onDetailSubmit}
        onDelete={onDetailDelete}
        loading={loading}
        error={error}
      />
    </>
  )
}

export default CountriesPage
